import React, { useEffect, useState } from 'react'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import {
  getInfoComplete,
  ModalBackground,
  SignInReturn,
  useAuth,
  useLocation,
  useResolution,
  Zone,
} from '@ecommerce/shared'
import DocumentType from '@ecommerce/shared/src/components/molecules/DocumentType'
import secrets from '@ecommerce/chile-customer-webapp/src/config/secrets'
import { IconClose, ArrowLeftIcon } from '@ecommerce/chile-customer-webapp/src/components/Icons'
import styled from 'styled-components'
import useSession from '@ecommerce/chile-customer-webapp/src/hooks/useSession'
import { useUserLoginHandler } from '@ecommerce/chile-customer-webapp/src/hooks/useUserLoginHandler'
import Button from '../../atoms/Button'
import TextField from '../../molecules/TextField'
import Select from '../../molecules/Select'
import { formatRut } from '../../../utils/forms'
import BirthDateInput from '../FormSignUp/BirthDateInput'
import { getSchema } from '../AuthWidget/libs'
import Anchor from '../../atoms/Anchor'
import customerApi, { updateCustomer } from '../../../services/CommerceLayer/customer'
import { useUI } from '../../../context/UI/ui.context'

import useSignIn from '../FormSignIn/useSignIn'

const { documentTypes } = secrets

export interface FormData {
  dni: string
  documentType?: string
  birthdate: string
  city: number | string
  market: number | string
}

export interface FormSignUpWithGoogleProps {
  onFinished?: (data?: SignInReturn) => Promise<void>
  onError?: () => void
  isCheckout?: boolean
  isLoadingUserData?: boolean
}

export interface FormAuthGoogleData {
  dni: string
  city: number | string
  market: number | string
  birthdate: string
  documentType?: string
  dniComplement?: string
  billingName?: string
}

const ModalWrapper = styled.div`
  .background {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .modal {
    height: auto;
    width: 383px;
    background: ${({ theme }) => theme.colors.white};
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: 24px 24px 32px 24px;
  }
`

const CustomForm = styled.form`
  display: flex;
  flex-direction: column;

  .modal-close {
    display: flex;
    justify-content: flex-end;
  }

  h2,
  p {
    margin: 0;
  }

  .section-title {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
  }

  .section-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 32px;
  }

  .signup-button {
    margin-top: 32px;
  }
`
const FormSignUpWithGoogle = (props: FormSignUpWithGoogleProps) => {
  const { isCheckout, onFinished, onError } = props

  const { userLoginCartHandler } = useUserLoginHandler()

  const {
    state: { markets },
    isBolivia,
  } = useLocation()
  const {
    closeSignUpWithGoogle,
    signUpWithGoogle: { data: googleData },
  } = useUI()

  const { isDesktop } = useResolution()

  const isInfoComplete = getInfoComplete()

  const { onSignInWithGoogle } = useAuth()

  const { resetAppState } = useSession()

  const [selectedDocumentType, setSelectedDocumentType] = useState('ci')
  const [citySelector, setCitySelector] = useState<string | number | undefined>()
  const [regionSelector, setRegionSelector] = useState<string | number | undefined>()
  const [cityOptions, setCityOptions] = useState<Array<{ label: string; value: number }>>([])
  const [cities, setCities] = useState<Zone[] | undefined>([])
  const [isCheckingDni, setIsCheckingDni] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [isLoadingUserData, setIsLoadingUserData] = useState<boolean>(false)

  const yupSchema = getSchema(isBolivia(), false, selectedDocumentType, true)
  const methods = useForm<FormData>({
    resolver: yupResolver(yupSchema),
    mode: 'all',
  })

  const { submitWithGoogle } = useSignIn({
    setIsLoadingUserData,
    userLoginCartHandler,
  })

  const { register, handleSubmit, reset, errors, watch, setValue, control, trigger } = methods

  const watchFields = watch()

  useEffect(() => {
    register({ name: 'market' })
    register({ name: 'city' })
    register({ name: 'birthdate' })
  }, [register])

  useEffect(() => {
    const zones = regionSelector ? markets.find((mk) => mk.id === regionSelector)?.zones : []
    setCities(zones)
  }, [regionSelector])

  useEffect(() => {
    const citySelectOptions = Object.values(cities ?? { value: 'empty', label: 'empty' }).map((city) => ({
      label: city.name,
      value: city.id,
    }))
    setCityOptions(citySelectOptions)
  }, [cities])

  useEffect(() => {
    reset({
      ...watchFields,
      documentType: isBolivia() ? 'ci' : 'rut',
    })
  }, [reset])

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true)
      let customerAttributes = {
        metadata: {
          dni: data.dni,
          birthdate: data.birthdate,
          city: Number(data.city),
          market: Number(data.market),
          ownerId: googleData?.ownerId,
          isInfoComplete: true,
        },
      }
      if (googleData) {
        const {
          data: {
            data: {
              attributes: { email, metadata },
            },
          },
        } = await customerApi.getCustomer({ ownerId: googleData?.ownerId })
        customerAttributes = {
          metadata: {
            ...metadata,
            ...customerAttributes.metadata,
          },
        }

        await updateCustomer({ ownerId: googleData?.ownerId, customer: customerAttributes })
        await submitWithGoogle()
        await onSignInWithGoogle({
          ownerId: googleData?.ownerId,
          token: googleData?.token,
        })
        closeSignUpWithGoogle()

        const customerData: SignInReturn = {
          data: {
            email,
            ...googleData.token,
            ...metadata,
            ...customerAttributes.metadata,
          },
        }

        if (onFinished) {
          await onFinished(customerData)
        }
      }
    } catch (err) {
      if (onError) onError()
    } finally {
      setLoading(false)
    }
  }

  const onRegionSelect = (value: string): void => {
    setRegionSelector(value)
    setValue('market', parseInt(value.valueOf().toString(), 10), { shouldValidate: true })
    if (isBolivia()) {
      const market = markets.filter((mk) => mk.id === parseInt(value, 10))[0]
      if (market && market.zones) {
        setValue('city', market?.zones[0]?.id ?? '', { shouldValidate: true })
        setCitySelector(value)
      }
    } else {
      setCitySelector('')
      setValue('city', 0, { shouldValidate: true })
    }
  }

  const onCitySelect = (value: string) => {
    setCitySelector(cities?.find((ct) => ct.id === parseInt(value.valueOf().toString(), 10))?.name ?? '')
    setValue('city', parseInt(value.valueOf().toString(), 10), { shouldValidate: true })
  }

  const onDateChange = async (value: string) => {
    setValue('birthdate', value)
    await trigger('birthdate')
  }

  const handleDniChange = (value: string) => {
    const formattedValue = formatRut(value)
    setValue('dni', formattedValue)
  }

  const isSubmitDisabled = !watchFields.dni || !watchFields.birthdate || !watchFields.city || !watchFields.market

  return (
    <ModalWrapper>
      <ModalBackground className="background">
        <div className="modal">
          <FormProvider {...methods}>
            <CustomForm onSubmit={handleSubmit(onSubmit)} noValidate>
              <Anchor
                data-test="modal-close"
                className="modal-close"
                onClick={async () => {
                  closeSignUpWithGoogle()
                  if (!isInfoComplete) {
                    if (isCheckout) {
                      await resetAppState('/checkout', { keepCart: true })
                    } else {
                      await resetAppState('/', { keepCart: true })
                    }
                    closeSignUpWithGoogle()
                  }
                }}
              >
                {isDesktop ? <IconClose /> : <ArrowLeftIcon />}
              </Anchor>

              <div className="section-title">
                <h2>Ya tienes una cuenta vinculada</h2>
                <p>Podrás acceder como usuario pero necesitas completar algunos datos.</p>
              </div>

              <div className="section-form">
                {isBolivia() ? (
                  <DocumentType
                    isVisible
                    isGuest
                    dni={watchFields.dni}
                    setIsCheckingDni={setIsCheckingDni}
                    selectedDocumentType={selectedDocumentType}
                    setSelectedDocumentType={setSelectedDocumentType}
                    types={documentTypes}
                  />
                ) : (
                  <Controller
                    control={control}
                    name="dni"
                    render={({ value }) => (
                      <TextField
                        label="RUT"
                        name="dni"
                        onChange={({ target: { value: dni } }) => handleDniChange(dni)}
                        type="text"
                        value={value || ''}
                        maxLength={12}
                        status={!errors.dni ? undefined : 'error'}
                        errorMessage={errors?.dni?.message}
                      />
                    )}
                  />
                )}

                <BirthDateInput
                  errorMessage={errors.birthdate?.message}
                  onDateChange={onDateChange}
                  status={!errors?.birthdate ? undefined : 'error'}
                />

                {!isBolivia() ? (
                  <>
                    <Select
                      label="Region"
                      name="market"
                      onSelect={onRegionSelect}
                      options={Object.values(markets).map((val) => ({
                        value: val.id,
                        label: val.name,
                      }))}
                      placeholder="Seleccionar región"
                      errorMessage={errors?.market?.message}
                      status={!errors.market ? undefined : 'error'}
                    />

                    <Select
                      label="Comuna"
                      name="ciudad"
                      onSelect={onCitySelect}
                      options={cityOptions}
                      value={citySelector as string}
                      placeholder="Seleccionar comuna"
                      disabled={!regionSelector}
                      status={!errors.city ? undefined : 'error'}
                      errorMessage={errors?.city?.message}
                    />
                  </>
                ) : (
                  <Select
                    label="Ciudad"
                    name="market"
                    onSelect={onRegionSelect}
                    options={Object.values(markets).map((val) => ({
                      value: val.id,
                      label: val.name,
                    }))}
                    placeholder="Seleccionar ciudad"
                    status={!errors.market ? undefined : 'error'}
                    errorMessage={errors?.market?.message}
                  />
                )}
              </div>

              <Button
                data-test="signup-with-google-submit"
                whileTap={{ scale: 0.9 }}
                type="submit"
                isDisabled={isSubmitDisabled || loading || isLoadingUserData}
                disabled={isSubmitDisabled || loading || isLoadingUserData}
                isLoading={loading}
                className="signup-button"
              >
                Confirmar
              </Button>
            </CustomForm>
          </FormProvider>
        </div>
      </ModalBackground>
    </ModalWrapper>
  )
}

export default FormSignUpWithGoogle
